import { Injectable } from '@angular/core';

import {
  Action,
  Selector,
  SelectorOptions,
  State,
  StateContext,
} from '@ngxs/store';

import { patch } from '@ngxs/store/operators';
import { UiSettingsControllerService } from '@osos/shared/api';
import {
  Language,
  LanguagePresenterModel,
  LocalizedValueModel,
  ManagedHttpContext,
} from '@osos/shared/types';
import { map, tap } from 'rxjs/operators';
import { LanguageFactoryService } from '../../factories/language-factory/language-factory.service';
import { AppState } from '../app/app.state';
import { DisplayAvailableLanguages, SetLanguage } from './language.actions';

export class LanguageStateModel {
  current: string;
  allLanguages: Language[];
  languages: LanguagePresenterModel[];
}

@State<LanguageStateModel>({
  name: 'language',
  defaults: {
    current: undefined,
    allLanguages: [],
    languages: [],
  },
})
@Injectable()
@SelectorOptions({ injectContainerState: false })
export class LanguageState {
  // private headlessLang = headLessQueryParams()?.language;
  /**
   * Selector to get current language from the application state
   * @param state: the application state
   */
  constructor(
    private settingService: UiSettingsControllerService,
    private languageFactoryService: LanguageFactoryService,
  ) {}
  @Selector([
    LanguageState,
    AppState.defaultLanguage,
    AppState.headlessConfig,
    LanguageState.defaultLanguage,
  ])
  static currentLanguage(
    state: LanguageStateModel,
    defaultLang: string,
    headlessConfig,
    defaultLangObj: Language,
  ): Language {
    return state?.allLanguages?.find(
      (lang) =>
        lang.languageName ===
        (headlessConfig?.language ??
          state.current ??
          defaultLang ??
          defaultLangObj.languageName),
    );
  }

  @Selector([LanguageState.currentLanguage])
  static currentLanguageRTLDirection(currentLang: Language): boolean {
    return currentLang.direction?.toLowerCase() === 'rtl';
  }

  /**
   * Selector to get all Languages from the application state
   * @param state the application all Languages state
   */
  @Selector([LanguageState])
  static supportedLangs(state: LanguageStateModel): Language[] {
    return state?.allLanguages;
  }

  /**
   * @description return object that map the locale and its label
   * EX: {en: {en: 'English', ar: 'الانكليزية'}}
   */
  @Selector([LanguageState.supportedLangs])
  static supportedLocaleLabels(
    languages: Language[],
  ): Record<string, LocalizedValueModel> {
    return languages?.reduce(
      (map, language) => ({
        ...map,
        [language.languageName]: language.label,
      }),
      {},
    );
  }
  /**
   * @description return array of supported locales
   * EX: ['ar', 'en']
   */
  @Selector([LanguageState.supportedLocaleLabels])
  static supportedLocales(
    languages: Record<string, LocalizedValueModel>,
  ): Array<string> {
    if (!languages || typeof languages !== 'object') {
      return [];
    }
    return Object.keys(languages);
  }

  /**
   * Selector to get the Default Language from the application state
   * @param state the application Default Language state
   */
  @Selector([LanguageState])
  static defaultLanguage(state: LanguageStateModel): Language {
    return state?.allLanguages?.find((lang) => lang.default);
  }
  @Selector([LanguageState])
  static selectLanguages(state: LanguageStateModel): LanguagePresenterModel[] {
    return state.languages;
  }

  @Action(SetLanguage)
  SetLanguage(
    { patchState }: StateContext<LanguageStateModel>,
    { language }: SetLanguage,
  ): void {
    patchState({ current: language });
  }
  @Action(DisplayAvailableLanguages)
  DisplayAvailableLanguages(
    { setState }: StateContext<LanguageStateModel>,
    {}: DisplayAvailableLanguages,
  ): void {
    this.settingService
      .getAvailableLanguages({
        context: new ManagedHttpContext({ bypassPublicMiddleware: true }),
      })
      .pipe(
        map((Languages) =>
          Languages.map((lang) =>
            this.languageFactoryService.createPresenterModel(lang),
          ),
        ),
        tap((languages) => {
          setState(
            patch<LanguageStateModel>({
              languages: languages,
              allLanguages: languages.map((lang) => ({
                id: lang.id,
                direction: lang.direction,
                languageName: lang.code,
                label: lang.name,
                default: lang.isDefault,
              })),
            }),
          );
        }),
      )
      .subscribe();
  }
}
